import { useEffect, useState, useCallback, useRef } from "react";
import dayjs from "dayjs";
import { PreferencesStep, CategoryStep, ScheduleStep, InfoStep, AgreementStep, CheckoutStep } from "./steps";
import { useStepper } from "../../common/Providers/StepperProvider/useStepper";
import { useStoreDetails } from "../../common/Providers/StoreDetailsProvider/UseStoreDetails";
import { useVehicleReservation } from "../../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import { useNavigate, useLocation } from "react-router-dom";
import { adminTry, testTokenVaild } from '../../api/Auth';
import { useSnackbar } from 'notistack';
import { getProductDetailByQRHash, checkQRQuickOrderAvailability, getStoreLocationsData } from '../../api/Product';

const steps = [
  { id: 0, key: "preferences", label: "Preferences", component: PreferencesStep, hideInStepper: true },
  // { id: 1, key: "category", label: "Category", component: CategoryStep, hideInStepper: true },
  { id: 1, key: "schedule", label: "Schedule", component: ScheduleStep, hideInStepper: false },
  { id: 2, key: "info", label: "Info", component: InfoStep, hideInStepper: false },
  { id: 3, key: "agreement", label: "Agreement", component: AgreementStep, hideInStepper: false },
  { id: 4, key: "checkout", label: "Checkout", component: CheckoutStep, hideInStepper: false },
];

const Rental = () => {
  const { VehicleReservationMain, initVehicleReservation, setVehicleReservationValue, addVehicleReservationItems } = useVehicleReservation();
  const { fetchCategories, storeDetails, setStoreValue } = useStoreDetails();
  const { activeStep, setSteps, steps: currentSteps, goTo } = useStepper();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [productDetail, setProductDetail] = useState<any | null>(null);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const hasAppliedPartnerRedirectRef = useRef(false);

  // Custom setSteps function that adjusts steps based on quick order mode
  const getCustomSteps = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);
    const q = queryParams.get("q");
    const p = queryParams.get("p");
    const r = queryParams.get("r");
    const isQuickOrder = (q === "1" || q === "true") && p;
    const isRentalMode = r === "1" || r === "true";
    const isPartnerSite = storeDetails.brands?.is_partner_site === true;
    const offerPickup = storeDetails.brands?.offer_pickup;

    if (isQuickOrder || isRentalMode) {
      return steps.filter(step => !["preferences"].includes(step.key));
    }

    // If is_partner_site=true + offer_pickup=false/0, skip preferences (delivery only, go straight to schedule)
    if (isPartnerSite && (offerPickup === false || offerPickup === 0)) {
      return steps.filter(step => !["preferences"].includes(step.key));
    }

    // All other cases (normal, partner with pickup, partner redirect) — show preferences
    return steps;
  }, [location.search, storeDetails.brands?.is_partner_site, storeDetails.brands?.offer_pickup]);

  useEffect(() => setSteps(getCustomSteps()), [setSteps, getCustomSteps]);

  // Set quick order and rental flow state based on URL parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const q = queryParams.get("q");
    const p = queryParams.get("p");
    const r = queryParams.get("r");
    const isQuickOrder = (q === "1" || q === "true") && !!p;
    const isRentalFlow = (r === "1" || r === "true") && !!p;
    
    // Only update if the values are different from current store values
    const currentIsQuickOrder = storeDetails.isQuickOrder;
    const currentIsRentalFlow = storeDetails.isRentalFlow;
    
    if (currentIsQuickOrder !== isQuickOrder || currentIsRentalFlow !== isRentalFlow) {
      setStoreValue('isQuickOrder', isQuickOrder);
      setStoreValue('isRentalFlow', isRentalFlow);
    }
  }, [location.search, storeDetails.isQuickOrder, storeDetails.isRentalFlow]);

  // Redirection logic for missing state (refresh)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const step = queryParams.get("step");
    if (step && step !== "preferences" && !VehicleReservationMain.mainArea) {
      navigate("/rental", { replace: true });
    }
  }, [location.search, VehicleReservationMain.mainArea, navigate]);

  // Pre-fetch categories can be automated in store but for now its rental specific
  useEffect(() => {
    if (!storeDetails.brand_id) return;
    fetchCategories();
  }, [storeDetails.brand_id]);

  const showError = (msg: string) => {
    enqueueSnackbar(msg, {
      variant: "error",
      style: { width: "300px" },
      autoHideDuration: 3000,
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get("id");
    const mode = queryParams.get("mode");
    const t = queryParams.get("t");

    // Helper: clear customer-related localStorage
    const clearCustomerStorage = () => {
      localStorage.removeItem("full-name");
      localStorage.removeItem("customerId");
      localStorage.removeItem("customer_email");
      localStorage.removeItem("customer_phone_number");
    };

    // Helper: save customer data
    const saveCustomerData = (data: any) => {
      localStorage.setItem("access-token", data.refreshToken);
      localStorage.setItem("full-name", data.fullName);
      localStorage.setItem("customerId", data.customerId);
      localStorage.setItem("customer_email", data.email);
      localStorage.setItem("customer_phone_number", data.phone_number);
    };

    const redirectToApp = () => {
      navigate(storeDetails.is_rental ? "/rental" : "/reservation");
    };

    // 1. Auto-login via URL params
    if (id && mode && t) {
      localStorage.setItem("access-token", t);
      clearCustomerStorage();

      adminTry({ customer_id: id, mode, t }, (jsonRes: any, status: any) => {
        switch (status) {
          case 200:
            saveCustomerData(jsonRes);
            initVehicleReservation();
            redirectToApp();
            break;

          case 403:
            showError("Incorrect password");
            break;

          case 404:
            // navigate('/usernotfound'); (optional)
            break;

          case 500:
            showError("Server Error");
            break;

          default:
            showError(jsonRes?.error || "Unknown Error");
            break;
        }
      });

      return; // stop further processing
    }

    // 2. Check existing token
    const accessToken = localStorage.getItem("access-token");
    if (accessToken) {
      testTokenVaild((jsonRes: any, status: any) => {
        if (status === 200) {
          // Check if we have quick order or rental flow parameters
          const queryParams = new URLSearchParams(location.search);
          const q = queryParams.get("q");
          const p = queryParams.get("p");
          const r = queryParams.get("r");
          const isQuickOrder = (q === "1" || q === "true") && p;
          const isRentalFlow = (r === "1" || r === "true") && p;
          
          // Only redirect to app if not in quick order or rental flow
          if (!isQuickOrder && !isRentalFlow) {
            redirectToApp();
          }
        }
      });
    }
  }, []);

  // Auto-set delivery mode when offer_pickup is false/0 (for partner sites)
  useEffect(() => {
    const offerPickup = storeDetails.brands?.offer_pickup;
    const isPartnerSite = storeDetails.brands?.is_partner_site === true;
    
    // Only apply auto-delivery logic if is_partner_site is true AND offer_pickup is false/0
    if (isPartnerSite && (offerPickup === false || offerPickup === 0) && storeDetails.id) {
      // Set mainArea to the store's location group or a default value
      if (!VehicleReservationMain.mainArea) {
        setVehicleReservationValue("mainArea", "charleston"); // Default area
      }
      
      // Set orderType to delivery
      if (!VehicleReservationMain.orderType) {
        setVehicleReservationValue("orderType", "delivery");
      }
      
      // Set pickup address to store address
      if (!VehicleReservationMain.manual_address && !VehicleReservationMain.selectedAddress) {
        const storeAddress = `${storeDetails.address_line1}, ${storeDetails.city}, ${storeDetails.state} ${storeDetails.postal_code}`;
        setVehicleReservationValue("use_manual", true);
        setVehicleReservationValue("manual_address", storeAddress);
      }
    }
  }, [storeDetails.brands?.offer_pickup, storeDetails.brands?.is_partner_site, storeDetails.id, storeDetails.address_line1, VehicleReservationMain.mainArea, VehicleReservationMain.orderType]);

  // Handle incoming partner redirect: auto-set location, orderType, hotel from URL params
  // Uses a ref to ensure this only runs once — location.search changes on every step navigation
  // (stepper appends ?step=...) which would re-fire this effect and overwrite any address
  // the user has already selected from the search results.
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const partnerLocationId = queryParams.get('partner_location');
    const partnerBrandId = queryParams.get('partner_brand');
    const orderTypeParam = queryParams.get('order_type');
    if (!partnerLocationId && !partnerBrandId) return;
    if (!storeDetails.brand_id) return;
    if (hasAppliedPartnerRedirectRef.current) return; // Only apply once

    getStoreLocationsData({ brand_id: storeDetails.brand_id, includeBrands: true }, (jsonRes: any, status: any) => {
      if (status !== 200 || !Array.isArray(jsonRes)) return;

      let matchingLocation = partnerLocationId
        ? jsonRes.find((loc: any) => String(loc.id) === partnerLocationId)
        : null;

      if (!matchingLocation && partnerBrandId) {
        matchingLocation = jsonRes.find((loc: any) =>
          loc.locationStores?.some((ls: any) => String(ls.brands?.id) === partnerBrandId)
        );
      }

      if (!matchingLocation) return;

      const resolvedOrderType = orderTypeParam || 'ride_off';
      setVehicleReservationValue("mainArea", matchingLocation.location_group);
      setVehicleReservationValue("subArea", 'downtown');
      setVehicleReservationValue("orderType", resolvedOrderType);
      setVehicleReservationValue("use_manual", true);

      if (resolvedOrderType === 'ride_off') {
        setVehicleReservationValue("hotel", matchingLocation.id);
      }

      const fullAddress = [
        matchingLocation.address_line1,
        matchingLocation.address_line2,
        matchingLocation.city,
        matchingLocation.state,
        matchingLocation.postal_code,
      ].filter(Boolean).join(', ');
      const addressWithName = matchingLocation.name
        ? `${matchingLocation.name} - ${fullAddress || matchingLocation.location || ""}`
        : (fullAddress || matchingLocation.location || "");

      // Only set partner default address if user hasn't already selected their own.
      // Without this guard, navigating between steps re-fires this effect (location.search
      // gains ?step=... on navigation) and overwrites the user's chosen address.
      if (!VehicleReservationMain.address_id) {
        setVehicleReservationValue("use_manual", true);
        setVehicleReservationValue("manual_address", addressWithName);
      }
      hasAppliedPartnerRedirectRef.current = true; // Mark as applied
    });
  }, [storeDetails.brand_id, location.search]);

  // Handle quick order flow with q and p params, and rental flow with r and p params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const q = queryParams.get("q");
    const p = queryParams.get("p");
    const r = queryParams.get("r");

    // Check if q is "1" or "true" and p exists (quick order)
    const shouldSkipStepsQuickOrder = (q === "1" || q === "true") && p;
    // Check if r is "1" or "true" and p exists (rental mode)
    const shouldSkipStepsRental = (r === "1" || r === "true") && p;

    if ((shouldSkipStepsQuickOrder || shouldSkipStepsRental) && !isLoadingProduct && !productDetail) {
      setIsLoadingProduct(true);

      const checkProductAvailability = async () => {
        try {
          await getProductDetailByQRHash(
            { qr_hash: p },
            (jsonRes: any, status: any) => {
              if (status === 200) {
                const resData = jsonRes.data || jsonRes;
                setProductDetail(resData);

                const mainArea = resData?.current_location_tbl?.locations?.location_group || null;

                if (shouldSkipStepsQuickOrder) { // now case
                  // Quick order flow - set all values
                  setVehicleReservationValue("mainArea", mainArea);
                  setVehicleReservationValue("subArea", 'downtown');
                  setVehicleReservationValue("orderType", "ride_off");
                  setVehicleReservationValue("use_manual", true);
                  setVehicleReservationValue("manual_address", resData?.current_location_tbl?.locations?.location || "");
                  setVehicleReservationValue("mainCategory", resData?.category_id);

                  // Get date and time from URL params (if provided by quick-order page)
                  const urlDate = queryParams.get("date");
                  const urlTime = queryParams.get("time");
                  
                  const now = dayjs();
                  let targetDate: string;
                  let targetTime: string;
                  
                  if (urlDate && urlTime) {
                    // Use date/time from URL (user confirmed wait option)
                    targetDate = urlDate;
                    targetTime = urlTime;
                    // console.log(`📅 Using URL params: date=${targetDate}, time=${targetTime}`);
                  } else {
                    // Use current date/time
                    targetDate = now.format('YYYY-MM-DD');
                    targetTime = now.format('HH:mm');
                    // console.log(`📅 Using current time: date=${targetDate}, time=${targetTime}`);
                  }

                  // Parse target date and time to set pickup
                  const [hours, minutes] = targetTime.split(':').map(Number);
                  const pickupDateTime = dayjs(targetDate).hour(hours).minute(minutes);
                  setVehicleReservationValue("pickup", pickupDateTime.toDate());

                  // Always set pickup_time from URL — availability was already verified on the QR page before redirect
                  setVehicleReservationValue("pickup_time", targetTime);
                  setVehicleReservationValue("no_available_time_slots", false);

                  const newItem = {
                    family_id: resData.family_id,
                    family: resData.family.family,
                    display_name: resData.family.display_name,
                    quantity: 1,
                    price_group_id: resData.line.price_group_id,
                    extras: [],
                    img_url: resData.family.img_url ?? "",
                    barcode: resData.barcode,
                    qr_hash: p
                  };

                  addVehicleReservationItems([newItem], "ride_off");
                } else if (shouldSkipStepsRental) { // later case
                  // Rental mode flow - set limited values
                  setVehicleReservationValue("mainArea", mainArea);
                  setVehicleReservationValue("subArea", 'downtown');
                  setVehicleReservationValue("mainCategory", resData?.category_id);

                  if (storeDetails.brands?.is_partner_site) {
                    setVehicleReservationValue("orderType", "ride_off");
                    setVehicleReservationValue("use_manual", true);
                    setVehicleReservationValue("manual_address", resData?.current_location_tbl?.locations?.location || "");
                  } else {
                    setVehicleReservationValue("orderType", "delivery");
                  }

                  const newItem = {
                    family_id: resData.family_id,
                    family: resData.family.family,
                    display_name: resData.family.display_name,
                    quantity: 1,
                    price_group_id: resData.line.price_group_id,
                    extras: [],
                    img_url: resData.family.img_url ?? "",
                    barcode: resData.barcode,
                    qr_hash: p
                  };

                  addVehicleReservationItems([newItem], storeDetails.brands?.is_partner_site ? "ride_off" : "delivery");
                }
                
                // Skip to first available step (which will be Schedule after filtering)
                goTo(0);
              } else {
                showError(jsonRes?.error || "Failed to load product details");
              }
              setIsLoadingProduct(false);
            }
          );
        } catch (err: any) {
          showError(err.message || "Something went wrong");
          setIsLoadingProduct(false);
        }
      };

      checkProductAvailability();
    }
  }, [location.search, goTo, isLoadingProduct, productDetail, storeDetails.brands?.is_partner_site]);

  const StepComponent = currentSteps[activeStep]?.component;

  return StepComponent ? <StepComponent /> : null;
};

export default Rental;
